import { useEffect, useState } from "react";
import { Button, Message, Select, Space, Table, Tag, Typography } from "@arco-design/web-react";
import { recentEvents, retryEvent, type EventSummary } from "../api/events.js";
import { parseApiError } from "../api/http.js";

const { Title } = Typography;

const STATUS_OPTIONS = [
  { label: "全部", value: "" },
  { label: "队列中", value: "queued" },
  { label: "处理中", value: "processing" },
  { label: "成功", value: "done" },
  { label: "死信", value: "dead" },
];

export function EventsPage(): JSX.Element {
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [status, setStatus] = useState<string>("");
  const [loading, setLoading] = useState(true);

  const load = (): void => {
    setLoading(true);
    recentEvents({ ...(status && { status }), limit: 100 })
      .then(setEvents)
      .catch((err: unknown) => Message.error(parseApiError(err).message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [status]);

  const handleRetry = async (key: string): Promise<void> => {
    try {
      await retryEvent(key);
      Message.success("已重新入队");
      load();
    } catch (err) {
      Message.error(parseApiError(err).message);
    }
  };

  return (
    <div className="os-stack">
      <div className="os-row" style={{ justifyContent: "space-between" }}>
        <Title heading={5} style={{ margin: 0 }}>
          事件队列
        </Title>
        <Space>
          <Select
            value={status}
            onChange={(v) => setStatus(v as string)}
            options={STATUS_OPTIONS}
            style={{ width: 140 }}
          />
          <Button onClick={load} loading={loading}>
            刷新
          </Button>
        </Space>
      </div>
      <Table
        data={events}
        rowKey="idempotency_key"
        loading={loading}
        pagination={false}
        columns={[
          { title: "幂等键", dataIndex: "idempotency_key", ellipsis: true, width: 250 },
          { title: "平台", dataIndex: "platform", width: 120 },
          { title: "订单号", dataIndex: "platform_order_id", width: 180 },
          { title: "事件类型", dataIndex: "raw_event_type", width: 200 },
          {
            title: "状态",
            dataIndex: "process_status",
            width: 100,
            render: (s: string) => {
              const color =
                s === "done" ? "green" : s === "dead" ? "red" : s === "queued" ? "orange" : "blue";
              return <Tag color={color}>{s}</Tag>;
            },
          },
          { title: "尝试次数", dataIndex: "attempts", width: 80 },
          {
            title: "接收时间",
            dataIndex: "received_at",
            width: 180,
            render: (v: number) => new Date(v).toLocaleString(),
          },
          {
            title: "错误",
            dataIndex: "error_message",
            ellipsis: true,
          },
          {
            title: "操作",
            width: 100,
            render: (_: unknown, row: EventSummary) =>
              row.process_status === "dead" ? (
                <Button size="mini" onClick={() => handleRetry(row.idempotency_key)}>
                  重试
                </Button>
              ) : null,
          },
        ]}
      />
    </div>
  );
}
